import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  Filter,
  FileDown,
  Eye,
  Calendar,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const actionNames = {
  create: 'Oluşturma',
  read: 'Görüntüleme',
  update: 'Güncelleme',
  delete: 'Silme',
  login: 'Giriş',
  logout: 'Çıkış',
  invite_sent: 'Davet Gönderme',
  invite_accepted: 'Davet Kabul',
};

const entityNames = {
  user: 'Kullanıcı',
  student: 'Öğrenci',
  assessment: 'Değerlendirme',
  intervention: 'Müdahale',
  session: 'Oturum',
  tenant: 'Kurum',
  invite: 'Davet',
};

export const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: 'all',
    entity_type: 'all',
    search: '',
  });

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.action !== 'all') params.append('action', filters.action);
      if (filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      
      const response = await fetch(`${API_URL}/api/audit-logs?${params}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters.action, filters.entity_type]);

  const filteredLogs = logs.filter(log => {
    if (!filters.search) return true;
    return log.user_email.toLowerCase().includes(filters.search.toLowerCase()) ||
           (log.entity_id && log.entity_id.toLowerCase().includes(filters.search.toLowerCase()));
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'create':
        return 'bg-green-100 text-green-700';
      case 'read':
        return 'bg-blue-100 text-blue-700';
      case 'update':
        return 'bg-yellow-100 text-yellow-700';
      case 'delete':
        return 'bg-red-100 text-red-700';
      case 'login':
      case 'logout':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            Denetim Kayıtları
          </h1>
          <p className="text-muted-foreground">
            Sistem üzerindeki tüm işlemleri görüntüleyin
          </p>
        </div>
        <Button variant="outline">
          <FileDown className="mr-2 h-4 w-4" />
          Dışa Aktar
        </Button>
      </div>

      {/* Privacy Notice */}
      <Card className="border-secondary/30 bg-secondary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-primary-900">KVKK Uyarısı</p>
              <p className="text-sm text-muted-foreground mt-1">
                Bu kayıtlar kişisel veri içermektedir. Yalnızca yetkili kişiler tarafından görüntülenebilir 
                ve yasal gereklilikler dışında paylaşılamaz.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="E-posta veya kayıt ID ara..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
                data-testid="log-search-input"
              />
            </div>
            <Select 
              value={filters.action} 
              onValueChange={(value) => setFilters({ ...filters, action: value })}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="İşlem Türü" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm İşlemler</SelectItem>
                {Object.entries(actionNames).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select 
              value={filters.entity_type} 
              onValueChange={(value) => setFilters({ ...filters, entity_type: value })}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Varlık Türü" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Varlıklar</SelectItem>
                {Object.entries(entityNames).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tarih</TableHead>
                <TableHead>Kullanıcı</TableHead>
                <TableHead>İşlem</TableHead>
                <TableHead>Varlık</TableHead>
                <TableHead>Detaylar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.log_id}>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {formatDate(log.created_at)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{log.user_email}</p>
                      <p className="text-xs text-muted-foreground">{log.user_role}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getActionBadgeColor(log.action)}>
                      {actionNames[log.action] || log.action}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm">{entityNames[log.entity_type] || log.entity_type}</p>
                      {log.entity_id && (
                        <p className="text-xs text-muted-foreground font-mono">{log.entity_id.slice(0, 12)}...</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {Object.keys(log.details).length > 0 ? (
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        Görüntüle
                      </Button>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filteredLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Kayıt bulunamadı
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
